import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface SectionProps {
  id?: string
  title?: string
  subtitle?: string
  background?: 'white' | 'light' | 'primary' | 'accent' | 'dark'
  centered?: boolean
  children: ReactNode
  className?: string
}

const backgroundStyles: Record<string, React.CSSProperties> = {
  white: { backgroundColor: '#ffffff' },
  light: { backgroundColor: '#f8fafc' },
  primary: { backgroundColor: '#003b7a', color: '#ffffff' },
  accent: { backgroundColor: '#00a3cc', color: '#ffffff' },
  dark: { backgroundColor: '#001f42', color: '#ffffff' },
}

export function Section({
  id,
  title,
  subtitle,
  background = 'white',
  centered = false,
  children,
  className,
}: SectionProps) {
  const isDark = background === 'primary' || background === 'accent' || background === 'dark'

  return (
    <section id={id} className={cn('section-padding', className)} style={backgroundStyles[background]}>
      <div className="container-main">
        {(title || subtitle) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={cn('mb-12', centered && 'text-center')}
          >
            {title && (
              <h2
                className="text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: isDark ? '#ffffff' : '#0f172a' }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className={cn('mt-4 text-lg', centered && 'mx-auto max-w-2xl')}
                style={{ color: isDark ? 'rgba(255,255,255,0.8)' : '#64748b' }}
              >
                {subtitle}
              </p>
            )}
          </motion.div>
        )}
        {children}
      </div>
    </section>
  )
}
